import React, { useEffect, useState } from 'react';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { Box, Button, FormControl, MenuItem, Popover, Select, Typography } from '@material-ui/core';

export type ProjectStatusFilter = 'all' | 'active' | 'archived';

export interface ProjectsTableFilters {
  filterStatus: ProjectStatusFilter;
  filterClient: string;
}

const statusOptions: { name: string; value: ProjectStatusFilter }[] = [
  { name: 'All', value: 'all' },
  { name: 'Active', value: 'active' },
  { name: 'Archived', value: 'archived' },
];

const useStyles = makeStyles(() =>
  createStyles({
    section: {
      padding: 16,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'left',
      minWidth: '270px',
    },
    inline: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'nowrap',
      paddingTop: 16,
    },
    button: {
      width: 100,
      height: 36,
    },
  }),
);

interface ProjectsTableFilterDialogProps {
  dialogOpen: boolean;
  handleClose: () => void;
  anchor: any;
  filters: ProjectsTableFilters;
  clientOptions: string[];
  updateFilters: (filters: ProjectsTableFilters) => void;
}

const ProjectsTableFilterDialog = (props: ProjectsTableFilterDialogProps) => {
  const { dialogOpen, handleClose, anchor, filters, clientOptions, updateFilters } = props;
  const classes = useStyles();

  const [filterStatus, setFilterStatus] = useState<ProjectStatusFilter>(filters.filterStatus);
  const [filterClient, setFilterClient] = useState<string>(filters.filterClient);

  useEffect(() => {
    setFilterStatus(filters.filterStatus);
    setFilterClient(filters.filterClient);
  }, [filters, dialogOpen]);

  const clearFilters = () => {
    setFilterStatus('all');
    setFilterClient('');
    updateFilters({ filterStatus: 'all', filterClient: '' });
    handleClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateFilters({ filterStatus, filterClient });
    handleClose();
  };

  return (
    <Popover
      open={dialogOpen}
      onClose={handleSubmit}
      anchorEl={anchor}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
    >
      <form onSubmit={handleSubmit}>
        <Box className={classes.section}>
          <Typography variant="body1">Status</Typography>
          <FormControl fullWidth variant="outlined" style={{ marginTop: 6 }}>
            <Select
              fullWidth
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as ProjectStatusFilter)}
              style={{ height: 38 }}
            >
              {statusOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.section}>
          <Typography variant="body1">Client</Typography>
          <FormControl fullWidth variant="outlined" style={{ marginTop: 6 }}>
            <Select
              fullWidth
              displayEmpty
              disabled={clientOptions.length === 0}
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value as string)}
              style={{ height: 38 }}
            >
              <MenuItem value="">Any</MenuItem>
              {clientOptions
                .sort((a, b) => a.localeCompare(b))
                .map((clientOption) => (
                  <MenuItem key={clientOption} value={clientOption}>
                    {clientOption}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.section}>
          <Box className={classes.inline}>
            <Button
              onClick={clearFilters}
              variant="outlined"
              className={classes.button}
              style={{ width: '50%' }}
            >
              Clear All
            </Button>
            <Button color="primary" type="submit" className={classes.button} variant="contained">
              Update
            </Button>
          </Box>
        </Box>
      </form>
    </Popover>
  );
};

export default ProjectsTableFilterDialog;
